import { BadgePercent, FileText, PackageCheck, TrendingUp } from 'lucide-react';
import GraficoBarras from '@/components/GraficoBarras';
import LogoFornecedor from '@/components/LogoFornecedor';
import { ETAPAS } from '@/features/cotacoes/situacao';
import { Cartao, CartaoIndicador, ErroDoBloco, Esqueleto, EsqueletoIndicador } from './componentes';
import { percentual, reais, reaisCompactos, reaisDoEixo } from './formatos';
import type { ResumoCompras } from './consultas';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "2026-08" vira "agosto de 2026" no tooltip. */
function mesPorExtenso(mes: string): string {
  const nomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  const [ano, numero] = mes.split('-');
  return `${nomes[Number(numero) - 1]} de ${ano}`;
}

/** "2025-10-01" vira "out/25": de onde a janela de 12 meses começa. */
function mesCurto(data: string): string {
  const [ano, mes] = data.split('-');
  return `${MESES[Number(mes) - 1]}/${ano.slice(2)}`;
}

/**
 * Os números da negociação: quanto os fechamentos tiraram do pacote inicial dos
 * fornecedores e quanto foi comprado — o argumento de que o sistema se paga.
 */
export function IndicadoresCompras({ dados, carregando }: { dados?: ResumoCompras; carregando: boolean }) {
  if (carregando || !dados) {
    return (
      <>
        <EsqueletoIndicador />
        <EsqueletoIndicador />
        <EsqueletoIndicador />
      </>
    );
  }

  const { totais, abertas } = dados;
  const dependemDeVoce = abertas.filter((c) => ETAPAS[c.situacao.etapa].tom !== 'espera').length;

  return (
    <>
      <CartaoIndicador
        icone={BadgePercent}
        rotulo="Ganho na negociação"
        valor={totais.fechamentos ? reais.format(totais.ganho) : '—'}
        variacao={
          totais.fechamentos
            ? {
                texto: percentual(totais.ganho_pct),
                direcao: totais.ganho > 0 ? 'desce' : totais.ganho < 0 ? 'sobe' : 'estavel',
                leitura: totais.ganho > 0 ? 'boa' : totais.ganho < 0 ? 'ruim' : 'neutra',
              }
            : null
        }
        detalhe={totais.fechamentos ? 'abaixo do pacote inicial · 12 meses' : 'Nenhum pacote fechado em 12 meses'}
        to="/cotacoes"
      />
      <CartaoIndicador
        icone={PackageCheck}
        rotulo="Comprado em 12 meses"
        valor={totais.fechamentos ? reaisCompactos.format(totais.valor_fechado) : '—'}
        detalhe={
          totais.fechamentos === 1 ? '1 pacote fechado' : totais.fechamentos ? `${totais.fechamentos} pacotes fechados` : 'Os fechamentos aparecem aqui'
        }
        to="/cotacoes"
      />
      <CartaoIndicador
        icone={FileText}
        rotulo="Cotações em andamento"
        valor={String(abertas.length)}
        detalhe={
          abertas.length === 0
            ? 'Nenhuma aberta agora'
            : dependemDeVoce === 0
              ? 'Todas aguardando fornecedor'
              : dependemDeVoce === 1
                ? '1 depende de você'
                : `${dependemDeVoce} dependem de você`
        }
        to="/cotacoes"
      />
    </>
  );
}

/** Ganho mês a mês e para quem foi o dinheiro, lado a lado. */
export function GraficosCompras({ dados, carregando, erro }: { dados?: ResumoCompras; carregando: boolean; erro: boolean }) {
  const inicio = dados ? mesCurto(dados.periodo.inicio) : '';
  const subtitulo = dados ? `De ${inicio} até hoje, pelo dia do fechamento.` : 'Últimos 12 meses.';

  if (erro) {
    return (
      <Cartao titulo="Ganho por mês" className="lg:col-span-3">
        <ErroDoBloco oQue="os números da negociação" />
      </Cartao>
    );
  }

  const semFechamento = !!dados && dados.totais.fechamentos === 0;
  const maiorValor = Math.max(1, ...(dados?.por_fornecedor ?? []).map((f) => f.valor));

  return (
    <>
      <Cartao titulo="Ganho por mês" subtitulo={subtitulo} className="lg:col-span-2">
        {carregando || !dados ? (
          <Esqueleto className="h-[220px] w-full" />
        ) : semFechamento ? (
          <Vazio icone={TrendingUp} texto="O ganho de cada pacote fechado aparece aqui, mês a mês." />
        ) : (
          <GraficoBarras
            pontos={dados.por_mes.map((m) => ({
              rotulo: `${mesPorExtenso(m.mes)} · ${m.fechamentos === 1 ? '1 fechamento' : `${m.fechamentos} fechamentos`}`,
              rotuloCurto: m.rotulo,
              valor: m.fechamentos ? m.ganho : null,
            }))}
            formatar={(v) => reaisDoEixo.format(v)}
            descricao="Ganho na negociação por mês, nos últimos 12 meses"
          />
        )}
      </Cartao>

      <Cartao titulo="Compras por fornecedor" subtitulo="Valor dos pacotes fechados em 12 meses.">
        {carregando || !dados ? (
          <div className="space-y-md">
            <Esqueleto className="h-[36px] w-full" />
            <Esqueleto className="h-[36px] w-full" />
            <Esqueleto className="h-[36px] w-full" />
          </div>
        ) : semFechamento ? (
          <Vazio icone={PackageCheck} texto="Com quem cada pacote foi fechado aparece aqui." />
        ) : (
          <ul className="space-y-md">
            {dados.por_fornecedor.map((fornecedor) => (
              <li key={fornecedor.apelido}>
                <div className="flex items-center justify-between gap-sm text-[13px]">
                  <span className="flex min-w-0 items-center gap-sm">
                    <LogoFornecedor logoUrl={fornecedor.logo_url} apelido={fornecedor.apelido} tamanho="sm" className="border border-border" />
                    <span className="truncate font-bold text-ink">{fornecedor.apelido}</span>
                  </span>
                  <span className="shrink-0 tabular-nums text-ink">{reaisCompactos.format(fornecedor.valor)}</span>
                </div>
                <div className="mt-xs h-[8px] rounded-full bg-surface-subtle" title={reais.format(fornecedor.valor)}>
                  <div
                    className="h-full rounded-full bg-accent"
                    style={{ width: `${Math.max(2, (fornecedor.valor / maiorValor) * 100)}%` }}
                  />
                </div>
                <p className="mt-[2px] text-[12px] text-muted">
                  {fornecedor.fechamentos === 1 ? '1 pacote' : `${fornecedor.fechamentos} pacotes`}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </>
  );
}

function Vazio({ icone: Icone, texto }: { icone: typeof TrendingUp; texto: string }) {
  return (
    <div className="flex min-h-[160px] flex-1 flex-col items-center justify-center rounded-md border border-dashed border-border px-md py-lg text-center">
      <Icone size={22} className="text-muted" />
      <p className="mt-sm text-[13px] text-muted">{texto}</p>
    </div>
  );
}
